// ────────────────────────────────────────────────────────────────────────────
// Settings → Workflows.
//
// Three candidate layouts were built over the same data and compared side by
// side before anything was stored — the Documents builder was started the same
// way and it saved rebuilding the editor twice. The Flow view won; the States
// and Matrix views and the switcher between them are gone.
//
// The STATE MACHINE is code (`mockWorkflow.ts`) and the POLICY is stored per
// organisation — see `policyMapping.ts` for why the split falls there.
//
// Two headings (Order / Movement), but THREE state machines: Movement splits
// into the trip and its legs. (Payment isn't a workflow — invoicing rides on the
// customer's payment terms + invoice approval; the one operational gate is the
// `customerPaid` requirement.) The track switch appears only where a heading
// holds more than one.
// ────────────────────────────────────────────────────────────────────────────
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganisation } from '@my/directory-api';
import { useSaveWorkflowPolicy, useWorkflowPolicies } from '@my/tms-api';
import { Button, ErrorBanner, Loading, PageHeader, cn, showToast } from '@my/ui';
import { Info, Save, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { TmsHistoryButton } from '@/components/HistoryButtons';
import { FlowLayout } from './layouts/FlowLayout';
import {
  withInvoiceableFrom,
  WORKFLOW_GROUPS,
  type Workflow,
  type WorkflowGroup,
  type WorkflowState,
  type WorkflowTransition,
} from './lib/mockWorkflow';
import { applyPolicy, toPolicyConfig } from './lib/policyMapping';
import { parseOrderReferenceLabels } from '@/utils/orderReferenceLabels';

export function WorkflowsPage() {
  const { user } = useAuth0();
  const organisationId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { data: policies, isLoading, error } = useWorkflowPolicies(organisationId);
  const savePolicy = useSaveWorkflowPolicy();

  // The reference picker offers the slots THIS organisation configured, named
  // the way it named them — the same source the Order Details page reads.
  const organisationQuery = useOrganisation(organisationId);
  const referenceLabels = useMemo(
    () => parseOrderReferenceLabels(organisationQuery.data?.tmsOrderReferenceLabelsJson),
    [organisationQuery.data?.tmsOrderReferenceLabelsJson],
  );

  const [group, setGroup] = useState<WorkflowGroup>(WORKFLOW_GROUPS[0]!);
  // The whole track is the draft. Edits are local until Save, exactly like the
  // Documents builder — a settings screen that writes on every keystroke gives
  // an operator no way to change their mind.
  const [track, setTrack] = useState<Workflow | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  /**
   * The stored policy laid over the code's state machine. Structure comes from
   * `WORKFLOW_GROUPS`; conditions, state behaviour and the cancellation policy
   * come from the API — including the shipped defaults, which the backend
   * returns when an organisation has never customised a track.
   */
  const configured = useMemo(() => {
    const byTrack = new Map((policies ?? []).map((p) => [p.trackKey, p]));
    return WORKFLOW_GROUPS.map((g) => ({
      ...g,
      tracks: g.tracks.map((w) => {
        const policy = byTrack.get(w.key);
        return policy ? applyPolicy(w, policy.config) : w;
      }),
    }));
  }, [policies]);

  const activeGroup = configured.find((g) => g.key === group.key) ?? configured[0]!;

  // Seed the draft once the policy lands, and re-seed if it is refetched while
  // the operator has no unsaved work. Guarded on `dirty` so a background
  // refetch cannot discard edits mid-sentence.
  useEffect(() => {
    if (!policies || dirty) return;
    setTrack((current) => {
      const next = activeGroup.tracks.find((w) => w.key === current?.key) ?? activeGroup.tracks[0]!;
      setSelectedKey((key) => key ?? keyOf(next.transitions[0]));
      return next;
    });
  }, [policies, dirty, activeGroup]);

  // Looked up from the draft each render rather than held in state: a copy would
  // go stale the moment a condition changed, and the panel would edit a ghost.
  const selected = track?.transitions.find((t) => keyOf(t) === selectedKey) ?? null;

  const pickTrack = (w: Workflow) => {
    setTrack(w);
    setSelectedKey(keyOf(w.transitions[0]));
    setDirty(false);
  };

  const pickGroup = (g: WorkflowGroup) => {
    setGroup(g);
    // Take the CONFIGURED copy, not the structural one from the constant —
    // otherwise switching heading would show shipped defaults over the top of
    // an organisation's saved policy.
    const target = configured.find((x) => x.key === g.key) ?? g;
    pickTrack(target.tracks[0]!);
  };

  const changeTransition = (next: WorkflowTransition) => {
    setTrack((w) => (!w ? w : {
      ...w,
      transitions: w.transitions.map((t) => (keyOf(t) === keyOf(next) ? next : t)),
    }));
    setDirty(true);
  };

  const changeCancelRequirements = (next: WorkflowTransition) => {
    // The card edits a synthetic transition (see FlowLayout) — only its
    // requirement list is meaningful, and it lands on the track.
    setTrack((w) => (!w ? w : { ...w, cancelRequirements: next.requirements }));
    setDirty(true);
  };

  // The invoicing switch writes several states at once — an order billable
  // once work starts is still billable when it is done, so a gap in the middle
  // describes nothing real. See `withInvoiceableFrom`.
  const changeInvoiceableFrom = (key: string, value: boolean) => {
    setTrack((w) => (!w ? w : { ...w, states: withInvoiceableFrom(w, key, value) }));
    setDirty(true);
  };

  const changeState = (next: WorkflowState) => {
    setTrack((w) =>
      !w ? w : { ...w, states: w.states.map((st) => (st.key === next.key ? next : st)) },
    );
    setDirty(true);
  };

  const discard = () => {
    // Back to what is STORED, which is why this reads from `configured` rather
    // than from the constant — discarding to shipped defaults would quietly
    // undo an earlier save.
    const original = activeGroup.tracks.find((t) => t.key === track?.key);
    if (original) pickTrack(original);
  };

  const save = async () => {
    if (!organisationId || !track) return;
    try {
      await savePolicy.mutateAsync({
        organisationId,
        trackKey: track.key,
        config: toPolicyConfig(track),
      });
      setDirty(false);
      showToast({ type: 'success', message: `${track.label} workflow saved.` });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Could not save the workflow.',
      });
    }
  };

  if (isLoading || !track) {
    return (
      <div className="flex h-full flex-col px-2">
        <PageHeader
          title="Workflows"
          description="The states an order or a movement moves through — and what has to be true before each move."
        />
        {error ? <ErrorBanner message={error.message} /> : <Loading />}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title="Workflows"
        description="The states an order or a movement moves through — and what has to be true before each move."
      >
        {/* Both tracks at once. A policy row per track is an implementation
            detail; "who changed the rules" is one question. */}
        <TmsHistoryButton
          organisationId={organisationId}
          aggregateType="WorkflowPolicy"
          wholeType
          subject="Workflows"
          variant="subtle"
        />
      </PageHeader>
      {/* A refetch failing after the draft has loaded must not replace the
          screen — the operator may be mid-edit. */}
      {error && <ErrorBanner message={error.message} />}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {/* Which workflow */}
        <div className="flex gap-1">
          {configured.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => pickGroup(g)}
              title={g.description}
              className={cn(
                'rounded-full border px-3 py-1 text-xs',
                g.key === group.key
                  ? 'border-sky-300 bg-sky-50 text-sky-800'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50',
              )}
            >
              {g.label}
              {g.tracks.length > 1 && (
                <span className="ml-1 text-[10px] text-slate-400">·{g.tracks.length}</span>
              )}
            </button>
          ))}
        </div>

        {dirty && (
          <div className="ml-auto flex items-center gap-1">
            <Button size="xs" variant="ghost" leftIcon={Undo2} onClick={discard}>
              Discard
            </Button>
            <Button
              size="xs"
              leftIcon={Save}
              disabled={savePolicy.isPending}
              onClick={() => void save()}
            >
              {savePolicy.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {/* Track switch — only where the heading holds more than one machine. */}
      {activeGroup.tracks.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-1 border-b border-slate-200 pb-2">
          {activeGroup.tracks.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => pickTrack(w)}
              title={w.description}
              className={cn(
                'rounded px-2.5 py-1 text-xs',
                w.key === track.key
                  ? 'bg-slate-100 font-medium text-slate-800'
                  : 'text-slate-500 hover:bg-slate-50',
              )}
            >
              {w.label}
            </button>
          ))}
          <span className="ml-2 text-[11px] text-slate-400">{activeGroup.description}</span>
        </div>
      )}

      {/* Where a track is ahead of the data, say so on the track itself rather
          than letting someone configure rules nothing can evaluate. */}
      {track.note && (
        <p className="mb-3 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-600">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span>{track.note}</span>
        </p>
      )}

      <main className="flex min-h-0 flex-1 flex-col pb-4">
        <FlowLayout
          workflow={track}
          selected={selected}
          onSelect={(t) => setSelectedKey(keyOf(t))}
          onChangeTransition={changeTransition}
          onChangeCancelRequirements={changeCancelRequirements}
          onChangeState={changeState}
          onChangeInvoiceableFrom={changeInvoiceableFrom}
          referenceLabels={referenceLabels}
        />
      </main>
    </div>
  );
}

/** Transitions have no id of their own — from+to identifies one. */
function keyOf(t: WorkflowTransition | undefined): string | null {
  return t ? `${t.from}->${t.to}` : null;
}
